import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_2211773d-f0b3-4564-83a5-4cc7f60a1377/artifacts/bzxne9u9_6744c91694f9840615c75764.png";

const TermsAndConditions = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-[#0B1120] py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="flex items-center gap-4">
            <img src={LOGO_URL} alt="Aspire Partners" className="h-10" />
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-[#0099CC] hover:text-[#007EA8] mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold text-[#0F172A] mb-4">
              Terms & Conditions
            </h1>
            <p className="text-slate-500 mb-12">
              <strong>Last Updated:</strong> December 11, 2025
            </p>

            <div className="prose prose-slate max-w-none">
              <p className="text-lg text-slate-600 mb-8">
                Please read these Terms and Conditions ("Terms", "Terms of Use") carefully before using the website{" "}
                <a href="http://aspirepartnersusa.com" className="text-[#0099CC] hover:underline">
                  http://aspirepartnersusa.com
                </a>{" "}
                (the "Service") operated by Aspire Partners LLC ("we", "us", or "our").
              </p>

              <p className="text-slate-600 mb-8">
                Your access to and use of the Service is conditioned upon your acceptance of and compliance with these Terms. These Terms apply to all visitors, users, and others who access or use the Service.
              </p>

              <p className="text-slate-600 mb-8">
                By accessing or using the Service, you agree to be bound by these Terms. If you do not agree with any part of these Terms, you may not access the Service.
              </p>

              <hr className="my-10 border-slate-200" />

              <h3 className="text-2xl font-bold text-[#0F172A] mb-4">Accounts</h3>
              <p className="text-slate-600 mb-4">
                When you create an account with Aspire Partners LLC, you must provide information that is accurate, complete, and current at all times. Failure to do so constitutes a breach of these Terms and may result in immediate termination of your account.
              </p>
              <p className="text-slate-600 mb-4">
                You are responsible for safeguarding the password you use to access the Service and for all activities or actions that occur under your account, whether on our Service or a third-party service.
              </p>
              <p className="text-slate-600 mb-8">
                You agree not to disclose your password to any third party. You must notify us immediately if you become aware of any breach of security or unauthorized use of your account.
              </p>

              <hr className="my-10 border-slate-200" />

              <h3 className="text-2xl font-bold text-[#0F172A] mb-4">Intellectual Property</h3>
              <p className="text-slate-600 mb-4">
                The Service and its original content, features, and functionality are and will remain the exclusive property of Aspire Partners LLC and its licensors.
              </p>
              <p className="text-slate-600 mb-8">
                You may not reproduce, distribute, modify, or create derivative works of any content on the Service without prior written permission.
              </p>

              <hr className="my-10 border-slate-200" />

              <h3 className="text-2xl font-bold text-[#0F172A] mb-4">Links to Other Websites</h3>
              <p className="text-slate-600 mb-4">
                Our Service may contain links to third-party websites or services that are not owned or controlled by Aspire Partners LLC.
              </p>
              <p className="text-slate-600 mb-4">
                Aspire Partners LLC has no control over, and assumes no responsibility for, the content, privacy policies, or practices of any third-party websites or services. You further acknowledge and agree that Aspire Partners LLC shall not be responsible or liable, directly or indirectly, for any damage or loss caused or alleged to be caused by the use of or reliance on any such content, goods, or services available through any third-party websites or services.
              </p>
              <p className="text-slate-600 mb-8">
                We strongly recommend that you review the terms and conditions and privacy policies of any third-party websites you visit.
              </p>

              <hr className="my-10 border-slate-200" />

              <h3 className="text-2xl font-bold text-[#0F172A] mb-4">Termination</h3>
              <p className="text-slate-600 mb-4">
                We may terminate or suspend your access to the Service immediately, without prior notice or liability, for any reason whatsoever, including if you breach these Terms.
              </p>
              <p className="text-slate-600 mb-4">
                Upon termination, your right to use the Service will immediately cease. If you wish to terminate your account, you may simply discontinue using the Service.
              </p>
              <p className="text-slate-600 mb-8">
                All provisions of these Terms that by their nature should survive termination shall survive termination, including, but not limited to, ownership provisions, warranty disclaimers, indemnity, and limitations of liability.
              </p>

              <hr className="my-10 border-slate-200" />

              <h3 className="text-2xl font-bold text-[#0F172A] mb-4">Disclaimer</h3>
              <p className="text-slate-600 mb-4">
                Your use of the Service is at your sole risk. The Service is provided on an <strong>"AS IS"</strong> and <strong>"AS AVAILABLE"</strong> basis.
              </p>
              <p className="text-slate-600 mb-8">
                The Service is provided without warranties of any kind, whether express or implied, including but not limited to implied warranties of merchantability, fitness for a particular purpose, non-infringement, or course of performance.
              </p>

              <hr className="my-10 border-slate-200" />

              <h3 className="text-2xl font-bold text-[#0F172A] mb-4">Governing Law</h3>
              <p className="text-slate-600 mb-4">
                These Terms shall be governed and construed in accordance with the laws of the United States, without regard to its conflict of law provisions.
              </p>
              <p className="text-slate-600 mb-4">
                Our failure to enforce any right or provision of these Terms shall not be considered a waiver of those rights. If any provision of these Terms is held to be invalid or unenforceable by a court, the remaining provisions will remain in full force and effect.
              </p>
              <p className="text-slate-600 mb-8">
                These Terms constitute the entire agreement between us regarding the Service and supersede any prior agreements we may have regarding the Service.
              </p>

              <hr className="my-10 border-slate-200" />

              <h3 className="text-2xl font-bold text-[#0F172A] mb-4">Changes to These Terms</h3>
              <p className="text-slate-600 mb-4">
                We reserve the right, at our sole discretion, to modify or replace these Terms at any time.
              </p>
              <p className="text-slate-600 mb-4">
                If a revision is considered material, we will attempt to provide at least 30 days' notice prior to the new terms taking effect. What constitutes a material change will be determined at our sole discretion.
              </p>
              <p className="text-slate-600 mb-8">
                By continuing to access or use the Service after any revisions become effective, you agree to be bound by the updated Terms. If you do not agree to the new Terms, you must stop using the Service.
              </p>

              <hr className="my-10 border-slate-200" />

              <h3 className="text-2xl font-bold text-[#0F172A] mb-4">Contact Us</h3>
              <p className="text-slate-600 mb-4">
                If you have any questions about these Terms and Conditions, please contact us at:
              </p>
              <p className="text-slate-600">
                <strong>Email:</strong>{" "}
                <a href="mailto:support@aspirepartnersusa.com" className="text-[#0099CC] hover:underline">
                  support@aspirepartnersusa.com
                </a>
              </p>
            </div>
          </motion.div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#0B1120] py-12 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <img src={LOGO_URL} alt="Aspire Partners" className="h-10" />
            <p className="text-slate-400 text-sm">
              © {new Date().getFullYear()} Aspire Partners. All rights reserved.
            </p>
            <div className="flex gap-6">
              <a href="#" className="text-slate-400 hover:text-[#0099CC] text-sm transition-colors">
                Privacy Policy
              </a>
              <Link to="/terms-and-conditions" className="text-slate-400 hover:text-[#0099CC] text-sm transition-colors">
                Terms & Conditions
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsAndConditions;
